// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Knowledge index generations.
 *
 * `OPS-038` requires index completeness evidence — document and chunk counts,
 * the embedding model, and the generation identifier — before an operator can
 * reason about a traffic switch. Counts are nullable in the contract because a
 * missing count must never be rendered as zero (`ADM-014`, `KNW-009`).
 */

import {
  capabilities,
  HealthBadge,
  queryKeys,
  StateBadge,
  type KnowledgeCollection,
} from '@eai/ui-shared';
import type { ReactNode } from 'react';

import { ResourceScreen, useControlPlaneQuery } from '../app-shell';

/** Format a nullable count without inventing a zero. */
export function formatCount(value: number | null): string {
  return value === null ? 'Not reported' : new Intl.NumberFormat().format(value);
}

/** Knowledge index generation screen. */
export function KnowledgeScreen(): ReactNode {
  const query = useControlPlaneQuery(
    (scope) => queryKeys.controlPlane.knowledgeCollections(scope),
    (client, signal) => client.listKnowledgeCollections({ signal }),
  );

  return (
    <ResourceScreen<KnowledgeCollection>
      title="Knowledge"
      description="Index generations with their completeness evidence. A missing count is reported as unknown, never as zero."
      requiredCapability={capabilities.knowledgeRead}
      area="Knowledge"
      query={query}
      rowKey={(row) => row.collection_id}
      emptyTitle="No knowledge collection exists"
      emptyDescription="Ingest an authorised source to create the first index generation."
      columns={[
        { key: 'name', header: 'Collection', render: (row) => row.name },
        { key: 'generation', header: 'Generation', render: (row) => row.generation },
        {
          key: 'documents',
          header: 'Documents',
          numeric: true,
          render: (row) => formatCount(row.document_count),
        },
        {
          key: 'chunks',
          header: 'Chunks',
          numeric: true,
          render: (row) => formatCount(row.chunk_count),
        },
        {
          key: 'embedding',
          header: 'Embedding model',
          render: (row) => row.embedding_model ?? 'Not reported',
        },
        {
          key: 'classification',
          header: 'Classification',
          render: (row) =>
            row.classification === null ? (
              'Not reported'
            ) : (
              <StateBadge
                label={row.classification.level}
                tone="caution"
                subject={`${row.name} classification`}
              />
            ),
        },
        {
          key: 'health',
          header: 'Index health',
          render: (row) => <HealthBadge health={row.health} subject={row.name} />,
        },
      ]}
    />
  );
}
